import type { MessageProps } from './Message.types'
import {
  MessageButton,
  MessageButtonsContent,
  MessageEndContent,
  MessageEndText,
  MessageEndTitle,
  MessageIcon,
  MessageImage,
  MessageLoader,
  MessageText,
  MessageTitle,
  MessageWrapper
} from './Message.styles'
import { isString } from '../../../utils'
import Icon from '../Icon'
import useId from '../../hooks/useId'

export const Message = ({
  className = '',
  title = '',
  message = '',
  endTitle = '',
  endMessage = '',
  image = '',
  icon,
  styles = {},
  data = {},
  buttons,
  buttonsHorizontal,
  loader = false,
  children,
  'data-testid': testid = 'message',
  ...props
}: MessageProps) => {
  const { getTestId } = useId(testid)
  const { value: titleValue, ...titleProps } = isString(title)
    ? { value: title }
    : title
  const { value: messageValue, ...messageProps } = isString(message)
    ? { value: message }
    : message
  const { value: endTitleValue, ...endTitleProps } = isString(endTitle)
    ? { value: endTitle }
    : endTitle
  const { value: endMessageValue, ...endMessageProps } = isString(endMessage)
    ? { value: endMessage }
    : endMessage
  const { src: imageSrc, ...imageProps } = isString(image)
    ? { src: image }
    : image
  const iconProps = isString(icon) ? { name: icon, color: '#979D9E' } : icon
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleButtonAction = (actions: any) => {
    if (actions) {
      actions({ data })
    }
  }

  return (
    <MessageWrapper
      className={`Message ${className}`}
      sx={styles}
      data-testid={getTestId()}
      {...props}>
      {!!icon && (
        <MessageIcon className="Icon" fontSize={iconProps?.size}>
          <Icon data-testid={getTestId('icon')} {...iconProps} />
        </MessageIcon>
      )}
      {!!imageSrc && (
        <MessageImage
          className="Image"
          src={imageSrc}
          data-testid={getTestId('image')}
          {...imageProps}
        />
      )}
      {!!loader && (
        <MessageLoader className="Loader" data-testid={getTestId('loader')} />
      )}
      {!!titleValue && (
        <MessageTitle
          className="Title"
          data-testid={getTestId('title')}
          {...titleProps}>
          {titleValue}
        </MessageTitle>
      )}
      {!!messageValue && (
        <MessageText
          className="Message"
          data-testid={getTestId('message')}
          {...messageProps}>
          {messageValue}
        </MessageText>
      )}
      {!!buttons && (
        <MessageButtonsContent
          className="Buttons"
          buttonsHorizontal={buttonsHorizontal}>
          {buttons.map((button, index) => (
            <MessageButton
              key={button.label}
              onClick={() => handleButtonAction(button.actions)}
              data-testid={getTestId(`button-${index}`)}
              fullWidth
              {...button}
            />
          ))}
        </MessageButtonsContent>
      )}
      {(!!endTitleValue || !!endMessageValue) && (
        <MessageEndContent className="EndContent" gap={props.gap}>
          {!!endTitleValue && (
            <MessageEndTitle
              className="EndTitle"
              data-testid={getTestId('end-title')}
              {...endTitleProps}>
              {endTitleValue}
            </MessageEndTitle>
          )}
          {!!endMessageValue && (
            <MessageEndText
              className="EndMessage"
              data-testid={getTestId('end-message')}
              {...endMessageProps}>
              {endMessageValue}
            </MessageEndText>
          )}
        </MessageEndContent>
      )}
      {children}
    </MessageWrapper>
  )
}

export default Message
